// =======================
// src/componentes/RegistraAto.js
// =======================
import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { api } from "@api/api";
import { formatarPrazoValidade } from "./comuns/prazoValidade";
import { ajustarTamanhoFonteAnalista } from "../estilos/global";

const estadoInicialAtos = {
  data: "",
  pagina: "",
  coluna: "",
  orgao: "",
  numeroPortaria: "",
  numeroParecer: "",
  nSei: "",
  corpoTexto: "",
  retificacao: false,
  validadeInicio: "",
  validadeFim: "",
};

export default function RegistroAto({
  onAtoRegistrado,
  atoParaEditar,
  onEdicaoEncerrada,
}) {
  const [open, setOpen] = useState(false);
  const [tipo, setTipo] = useState("");
  const [isNSeiChecked, setIsNSeiChecked] = useState(false);
  const [isValidadeChecked, setIsValidadeChecked] = useState(false);
  const [ordemOpcoes, setOrdemOpcoes] = useState([]);
  const [atoExpirado, setAtoExpirado] = useState(false);
  const [erros, setErros] = useState({});
  const [erroEnvio, setErroEnvio] = useState("");
  const [avisoAberto, setAvisoAberto] = useState(false);
  const [confirmAberto, setConfirmAberto] = useState(false);
  const [cancelamentoAberto, setCancelamentoAberto] = useState(false);

  const [atosData, setAtosData] = useState({ ...estadoInicialAtos });

  const modoEdicao = Boolean(atoParaEditar);
  const prazoValidade = formatarPrazoValidade(
    atosData.validadeInicio,
    atosData.validadeFim,
  );

  useEffect(() => {
    const handler = () => {
      setErroEnvio("");
      setOpen(true);
    };

    document.addEventListener("abrirDialogoRegistro", handler);

    return () => document.removeEventListener("abrirDialogoRegistro", handler);
  }, []);

  useEffect(() => {
    if (!atoParaEditar) return;

    setTipo(atoParaEditar.tipo || "");
    const temNSei = Boolean(atoParaEditar.incluir_n_sei);

    const temValidade = Boolean(
      atoParaEditar.inicio_da_validade || atoParaEditar.final_da_validade,
    );

    setIsNSeiChecked(temNSei);
    setIsValidadeChecked(temValidade);

    setOrdemOpcoes([
      ...(temNSei ? ["sei"] : []),
      ...(temValidade ? ["validade"] : []),
    ]);

    setAtosData({
      data: atoParaEditar.data ? String(atoParaEditar.data).slice(0, 10) : "",
      pagina: atoParaEditar.pagina || "",
      coluna: atoParaEditar.coluna || "",
      orgao: atoParaEditar.orgao_emissor || "",
      numeroPortaria:
        atoParaEditar.numero_parecer_ou_portaria !== null &&
        atoParaEditar.numero_parecer_ou_portaria !== undefined
          ? String(atoParaEditar.numero_parecer_ou_portaria)
          : "",
      numeroParecer: "",
      nSei: atoParaEditar.incluir_n_sei || "",
      corpoTexto: atoParaEditar.corpo_do_texto || "",
      retificacao: Boolean(atoParaEditar.retificacao),
      validadeInicio: atoParaEditar.inicio_da_validade
        ? String(atoParaEditar.inicio_da_validade).slice(0, 10)
        : "",
      validadeFim: atoParaEditar.final_da_validade
        ? String(atoParaEditar.final_da_validade).slice(0, 10)
        : "",
    });

    setErros({});
    setErroEnvio("");

    setOpen(true);
  }, [atoParaEditar]);

  useEffect(() => {
    setErros((prevErros) =>
      Object.keys(prevErros).length > 0 ? {} : prevErros,
    );
  }, [tipo, atosData, isNSeiChecked, isValidadeChecked]);

  useEffect(() => {
    const { validadeInicio, validadeFim } = atosData;

    if (!validadeInicio || !validadeFim) {
      setAtoExpirado(false);
      return;
    }

    const inicio = new Date(`${validadeInicio}T00:00:00`);
    const fim = new Date(`${validadeFim}T00:00:00`);

    if (
      Number.isNaN(inicio.getTime()) ||
      Number.isNaN(fim.getTime()) ||
      inicio > fim
    ) {
      setAtoExpirado(false);
      return;
    }

    setAtoExpirado(fim.getFullYear() < new Date().getFullYear());
  }, [atosData.validadeInicio, atosData.validadeFim]);

  const limparFormulario = () => {
    setTipo("");
    setIsNSeiChecked(false);
    setIsValidadeChecked(false);
    setOrdemOpcoes([]);
    setAtoExpirado(false);
    setErros({});
    setErroEnvio("");
    setAtosData({ ...estadoInicialAtos });
  };

  const handleSubmit = () => {
    const novosErros = {};

    setErroEnvio("");

    if (!tipo) novosErros.tipo = "Campo obrigatório";
    if (!atosData.data) novosErros.data = "Campo obrigatório";
    if (!atosData.pagina) novosErros.pagina = "Campo obrigatório";
    if (!atosData.coluna) novosErros.coluna = "Campo obrigatório";
    if (!atosData.orgao.trim()) novosErros.orgao = "Campo obrigatório";

    if (!atosData.numeroPortaria.trim()) {
      novosErros.numeroPortaria = "Campo obrigatório";
    }

    if (!atosData.corpoTexto.trim()) {
      novosErros.corpoTexto = "Campo obrigatório";
    }

    if (isNSeiChecked && !atosData.nSei.trim()) {
      novosErros.nSei = "Campo obrigatório";
    }

    if (isValidadeChecked && !atosData.validadeInicio) {
      novosErros.validadeInicio = "Campo obrigatório";
    }

    if (isValidadeChecked && !atosData.validadeFim) {
      novosErros.validadeFim = "Campo obrigatório";
    }

    setErros(novosErros);

    if (Object.keys(novosErros).length > 0) {
      setAvisoAberto(true);
      return;
    }

    setConfirmAberto(true);
  };

  const confirmarRegistro = async () => {
    setConfirmAberto(false);
    setErroEnvio("");

    const pathname = window.location.pathname;
    const pathParts = pathname.split("/");
    const lastPart = pathParts[pathParts.length - 1];

    const id = Number.isNaN(Number(lastPart)) ? null : parseInt(lastPart, 10);

    let id_mantenedora = null;
    let id_escola = null;

    if (pathname.includes("/perfil-mantenedora/")) {
      id_mantenedora = id;
    } else if (pathname.includes("/perfil-escola/")) {
      id_escola = id;
    }

    const atoData = {
      tipo,
      data: atosData.data,
      pagina: atosData.pagina ? parseInt(atosData.pagina, 10) : null,
      coluna: atosData.coluna ? parseInt(atosData.coluna, 10) : null,
      orgao_emissor: atosData.orgao.trim(),
      numero_parecer_ou_portaria: atosData.numeroPortaria
        ? parseInt(atosData.numeroPortaria, 10)
        : null,
      incluir_n_sei: isNSeiChecked ? atosData.nSei : null,
      corpo_do_texto: atosData.corpoTexto.trim(),
      inicio_da_validade: isValidadeChecked ? atosData.validadeInicio : null,
      final_da_validade: isValidadeChecked ? atosData.validadeFim : null,
      retificacao: atosData.retificacao,
      id_mantenedora,
      id_escola,
    };

    try {
      const perfil = pathname.split("/")[1];

      const url = modoEdicao
        ? api.updateAct(perfil, id, atoParaEditar.id)
        : api.fetchActs(perfil, id);

      const method = modoEdicao ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(atoData),
        credentials: "include",
      });

      if (!response.ok) {
        let mensagem = modoEdicao
          ? "Não foi possível atualizar o ato."
          : "Não foi possível registrar o ato.";

        try {
          const dadosErro = await response.json();

          if (Array.isArray(dadosErro?.detail)) {
            mensagem = dadosErro.detail
              .map((item) => item?.msg)
              .filter(Boolean)
              .join(" ");
          } else if (typeof dadosErro?.detail === "string") {
            mensagem = dadosErro.detail;
          } else if (typeof dadosErro?.message === "string") {
            mensagem = dadosErro.message;
          }
        } catch {
          // Mantém a mensagem padrão quando a API não retorna JSON.
        }

        throw new Error(mensagem);
      }

      await response.json();

      limparFormulario();

      if (onAtoRegistrado) {
        onAtoRegistrado();
      }

      if (modoEdicao && onEdicaoEncerrada) {
        onEdicaoEncerrada();
      }

      setOpen(false);
    } catch (error) {
      console.error("Erro ao salvar ato:", error);

      setErroEnvio(
        error?.message || "Ocorreu um erro inesperado ao salvar o ato.",
      );
    }
  };

  const solicitarCancelamento = () => {
    const formularioPossuiDados =
      Boolean(tipo) ||
      Boolean(atosData.data) ||
      Boolean(atosData.pagina) ||
      Boolean(atosData.coluna) ||
      Boolean(atosData.orgao?.trim()) ||
      Boolean(atosData.numeroPortaria?.trim()) ||
      Boolean(atosData.numeroParecer?.trim()) ||
      Boolean(atosData.nSei?.trim()) ||
      Boolean(atosData.corpoTexto?.trim()) ||
      Boolean(atosData.validadeInicio) ||
      Boolean(atosData.validadeFim) ||
      Boolean(atosData.retificacao) ||
      isNSeiChecked ||
      isValidadeChecked;

    if (!formularioPossuiDados) {
      setErros({});
      setErroEnvio("");
      setOpen(false);
      return;
    }

    setCancelamentoAberto(true);
  };

  const voltarAoFormulario = () => {
    setCancelamentoAberto(false);
  };

  const confirmarCancelamento = () => {
    setCancelamentoAberto(false);
    setOpen(false);
    limparFormulario();

    if (atoParaEditar && onEdicaoEncerrada) {
      onEdicaoEncerrada();
    }
  };

  const formatNSei = (value) => {
    return value
      .replace(/\D/g, "")
      .slice(0, 19)
      .replace(/(\d{4})(\d{2})(\d{7})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  };

  const handleNSeiChange = (event) => {
    const formattedValue = formatNSei(event.target.value);

    setAtosData((prev) => ({
      ...prev,
      nSei: formattedValue,
    }));
  };

  const handleValidadeChange = (event) => {
    const { name, value } = event.target;

    setAtosData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const tituloSecao = (titulo) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 1.25,
      }}
    >
      <Typography
        sx={{
          color: "primary.main",
          fontSize: ajustarTamanhoFonteAnalista("0.85rem"),
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {titulo}
      </Typography>

      <Divider
        sx={{
          flex: 1,
          borderColor: "#90caf9",
        }}
      />
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={solicitarCancelamento}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            maxHeight: "calc(100vh - 32px)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <DescriptionRoundedIcon
                sx={{
                  color: "primary.main",
                  fontSize: "1.6rem",
                  mt: 0.1,
                }}
              />

              <Box>
                <Typography
                  component="h2"
                  sx={{
                    color: "text.primary",
                    fontSize: ajustarTamanhoFonteAnalista("1.05rem"),
                    fontWeight: 700,
                    lineHeight: 1.25,
                  }}
                >
                  {modoEdicao ? "Editar ato" : "Registrar novo ato"}
                </Typography>

                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: ajustarTamanhoFonteAnalista("0.75rem"),
                    lineHeight: 1.35,
                    mt: 0.25,
                  }}
                >
                  {modoEdicao
                    ? "Revise e atualize os dados de publicação do ato."
                    : "Preencha os dados de publicação do ato."}
                </Typography>
              </Box>
            </Box>

            <IconButton
              aria-label="Fechar"
              onClick={solicitarCancelamento}
              size="small"
              sx={{
                color: "text.secondary",
                flexShrink: 0,
                "&:hover": {
                  color: "primary.main",
                  backgroundColor: "#eaf3fd",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.75,
            overflowY: "auto",
          }}
        >
          {erroEnvio && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {erroEnvio}
            </Alert>
          )}
          <Box component="section">
            {tituloSecao("Identificação do ato")}

            <Box
              sx={{
                p: 1.5,
                border: "1px solid #bbdefb",
                borderRadius: "6px",
                backgroundColor: "#f4f9ff",
              }}
            >
              <Grid container spacing={1.25} alignItems="flex-start">
                <Grid item xs={12} sm={2.3}>
                  <TextField
                    label="Data"
                    required
                    size="small"
                    error={Boolean(erros.data)}
                    helperText={erros.data || ""}
                    type="date"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={atosData.data}
                    onChange={(event) =>
                      setAtosData((prev) => ({
                        ...prev,
                        data: event.target.value,
                      }))
                    }
                  />
                </Grid>

                <Grid item xs={6} sm={1.2}>
                  <TextField
                    label="Página"
                    required
                    size="small"
                    error={Boolean(erros.pagina)}
                    helperText={erros.pagina || ""}
                    type="number"
                    fullWidth
                    inputProps={{ min: 1 }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={atosData.pagina}
                    onChange={(event) =>
                      setAtosData((prev) => ({
                        ...prev,
                        pagina: event.target.value,
                      }))
                    }
                  />
                </Grid>

                <Grid item xs={6} sm={1.2}>
                  <TextField
                    label="Coluna"
                    required
                    size="small"
                    error={Boolean(erros.coluna)}
                    helperText={erros.coluna || ""}
                    type="number"
                    fullWidth
                    inputProps={{ min: 1 }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={atosData.coluna}
                    onChange={(event) =>
                      setAtosData((prev) => ({
                        ...prev,
                        coluna: event.target.value,
                      }))
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={1.7}>
                  <FormControl
                    fullWidth
                    required
                    size="small"
                    error={Boolean(erros.tipo)}
                  >
                    <InputLabel shrink>Tipo</InputLabel>

                    <Select
                      value={tipo}
                      onChange={(event) => setTipo(event.target.value)}
                      label="Tipo"
                      notched
                    >
                      <MenuItem value="Parecer">Parecer</MenuItem>

                      <MenuItem value="Portaria">Portaria</MenuItem>
                    </Select>

                    {erros.tipo && (
                      <Typography
                        sx={{
                          color: "error.main",
                          fontSize: ajustarTamanhoFonteAnalista("0.7rem"),
                          mt: 0.5,
                          ml: 1.75,
                        }}
                      >
                        {erros.tipo}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={1.7}>
                  <TextField
                    label="Órgão emissor"
                    required
                    size="small"
                    error={Boolean(erros.orgao)}
                    helperText={erros.orgao || ""}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    placeholder="Ex: SRE"
                    value={atosData.orgao}
                    onChange={(event) =>
                      setAtosData((prev) => ({
                        ...prev,
                        orgao: event.target.value,
                      }))
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <TextField
                    label="Parecer/Portaria"
                    required
                    size="small"
                    error={Boolean(erros.numeroPortaria)}
                    helperText={erros.numeroPortaria || ""}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      inputMode: "numeric",
                    }}
                    placeholder="Ex: 123"
                    value={atosData.numeroPortaria}
                    onChange={(event) =>
                      setAtosData((prev) => ({
                        ...prev,
                        numeroPortaria: event.target.value.replace(/\D/g, ""),
                      }))
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Box component="section" sx={{ mt: 2 }}>
            {tituloSecao("Conteúdo do ato")}

            <TextField
              label="Corpo do texto"
              required
              size="small"
              error={Boolean(erros.corpoTexto)}
              helperText={erros.corpoTexto || ""}
              multiline
              minRows={5}
              maxRows={12}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              value={atosData.corpoTexto}
              onChange={(event) =>
                setAtosData((prev) => ({
                  ...prev,
                  corpoTexto: event.target.value,
                }))
              }
            />
          </Box>
          {/* Opções adicionais */}
          <Box component="section" sx={{ mt: 2 }}>
            {tituloSecao("Opções adicionais")}

            <Box
              sx={{
                p: 1.5,
                border: "1px solid #bbdefb",
                borderRadius: "6px",
                backgroundColor: "#f4f9ff",
              }}
            >
              {/* Interruptores */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  columnGap: 4,
                  rowGap: 0.5,
                  mb: isValidadeChecked || isNSeiChecked ? 1.5 : 0,
                }}
              >
                {/* Primeiro: SEI */}
                <FormControlLabel
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: ajustarTamanhoFonteAnalista("0.85rem"),
                      fontWeight: 600,
                    },
                  }}
                  control={
                    <Switch
                      size="small"
                      checked={isNSeiChecked}
                      onChange={(event) => {
                        const marcado = event.target.checked;

                        setIsNSeiChecked(marcado);

                        setOrdemOpcoes((ordemAtual) => {
                          if (marcado) {
                            return ordemAtual.includes("sei")
                              ? ordemAtual
                              : [...ordemAtual, "sei"];
                          }

                          return ordemAtual.filter((opcao) => opcao !== "sei");
                        });

                        if (!marcado) {
                          setErros((prev) => ({
                            ...prev,
                            nSei: undefined,
                          }));
                        }
                      }}
                    />
                  }
                  label="Incluir Nº SEI"
                />

                {/* Segundo: validade */}
                <FormControlLabel
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: ajustarTamanhoFonteAnalista("0.85rem"),
                      fontWeight: 600,
                    },
                  }}
                  control={
                    <Switch
                      size="small"
                      checked={isValidadeChecked}
                      onChange={(event) => {
                        const marcado = event.target.checked;

                        setIsValidadeChecked(marcado);

                        setOrdemOpcoes((ordemAtual) => {
                          if (marcado) {
                            return ordemAtual.includes("validade")
                              ? ordemAtual
                              : [...ordemAtual, "validade"];
                          }

                          return ordemAtual.filter(
                            (opcao) => opcao !== "validade",
                          );
                        });

                        if (!marcado) {
                          setErros((prev) => ({
                            ...prev,
                            validadeInicio: undefined,
                            validadeFim: undefined,
                          }));
                        }
                      }}
                    />
                  }
                  label="Definir validade"
                />
              </Box>

              {/* Campos ativados */}
              {(isValidadeChecked || isNSeiChecked) && (
                <Grid container spacing={1.25} alignItems="flex-start">
                  {ordemOpcoes.map((opcao) => {
                    if (opcao === "sei" && isNSeiChecked) {
                      return (
                        <Grid item xs={12} sm={4} key="sei">
                          <TextField
                            label="Número SEI"
                            required
                            size="small"
                            error={Boolean(erros.nSei)}
                            helperText={erros.nSei || ""}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={atosData.nSei}
                            onChange={handleNSeiChange}
                            placeholder="0000.00.0000000/0000-00"
                          />
                        </Grid>
                      );
                    }

                    if (opcao === "validade" && isValidadeChecked) {
                      return (
                        <React.Fragment key="validade">
                          <Grid item xs={12} sm={isNSeiChecked ? 8 / 3 : 4}>
                            <TextField
                              label="Início da validade"
                              required
                              size="small"
                              error={Boolean(erros.validadeInicio)}
                              helperText={erros.validadeInicio || ""}
                              type="date"
                              name="validadeInicio"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              value={atosData.validadeInicio}
                              onChange={handleValidadeChange}
                            />
                          </Grid>

                          <Grid item xs={12} sm={isNSeiChecked ? 8 / 3 : 4}>
                            <TextField
                              label="Fim da validade"
                              required
                              size="small"
                              error={Boolean(erros.validadeFim)}
                              helperText={erros.validadeFim || ""}
                              type="date"
                              name="validadeFim"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              value={atosData.validadeFim}
                              onChange={handleValidadeChange}
                            />
                          </Grid>

                          <Grid item xs={12} sm={isNSeiChecked ? 8 / 3 : 4}>
                            <TextField
                              label="Prazo de validade"
                              size="small"
                              fullWidth
                              value={prazoValidade}
                              InputLabelProps={{ shrink: true }}
                              InputProps={{ readOnly: true }}
                            />
                          </Grid>
                        </React.Fragment>
                      );
                    }

                    return null;
                  })}

                  {atoExpirado && isValidadeChecked && (
                    <Grid item xs={12}>
                      <Typography
                        sx={{
                          color: "error.main",
                          fontSize: ajustarTamanhoFonteAnalista("0.75rem"),
                          fontWeight: 600,
                        }}
                      >
                        Ato expirado!
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.25,
            gap: 1,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            onClick={solicitarCancelamento}
            variant="outlined"
            size="small"
            sx={{
              minWidth: 88,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "6px",
            }}
          >
            Cancelar
          </Button>

          <Button
            onClick={handleSubmit}
            variant="contained"
            size="small"
            startIcon={<SaveRoundedIcon />}
            sx={{
              minWidth: 118,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "6px",
            }}
          >
            {modoEdicao ? "Salvar alterações" : "Registrar ato"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={avisoAberto} onClose={() => setAvisoAberto(false)}>
        <DialogTitle>Campos obrigatórios</DialogTitle>

        <DialogContent>
          <Typography>
            Preencha todos os campos obrigatórios antes de salvar.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setAvisoAberto(false)}
            color="primary"
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmAberto} onClose={() => setConfirmAberto(false)}>
        <DialogTitle>
          {modoEdicao ? "Confirmar edição" : "Confirmar registro"}
        </DialogTitle>

        <DialogContent>
          <Typography>
            {modoEdicao
              ? "Deseja realmente salvar as alterações deste ato?"
              : "Deseja realmente registrar este ato?"}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmAberto(false)} color="secondary">
            Cancelar
          </Button>

          <Button
            onClick={confirmarRegistro}
            color="primary"
            variant="contained"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelamentoAberto}
        onClose={voltarAoFormulario}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: 520,
            borderTop: "4px solid #d32f2f",
            borderRadius: "6px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            px: 3,
            pt: 2.25,
            pb: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
            }}
          >
            <ErrorRoundedIcon
              color="error"
              sx={{
                fontSize: "1.75rem",
                flexShrink: 0,
              }}
            />

            <Typography
              component="h2"
              sx={{
                color: "text.primary",
                fontSize: ajustarTamanhoFonteAnalista("1.15rem"),
                fontWeight: 600,
                lineHeight: 1.3,
              }}
            >
              {modoEdicao ? "Cancelar edição?" : "Cancelar registro?"}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            px: 3,
            pt: 1,
            pb: 2,
          }}
        >
          <Typography
            sx={{
              color: "text.primary",
              fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
              lineHeight: 1.5,
              mb: 1.5,
            }}
          >
            {modoEdicao
              ? "Deseja realmente cancelar a edição deste ato?"
              : "Deseja realmente cancelar o registro deste ato?"}
          </Typography>

          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              border: "1px solid #ef9a9a",
              borderRadius: "6px",
              backgroundColor: "#ffebee",
            }}
          >
            <Typography
              sx={{
                color: "#c62828",
                fontSize: ajustarTamanhoFonteAnalista("0.9rem"),
                fontWeight: 600,
                lineHeight: 1.4,
              }}
            >
              Os dados informados até aqui serão perdidos.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pt: 0,
            pb: 2.5,
            gap: 1,
            flexDirection: {
              xs: "column-reverse",
              sm: "row",
            },
            "& > :not(style) ~ :not(style)": {
              ml: 0,
            },
          }}
        >
          <Button
            onClick={voltarAoFormulario}
            variant="outlined"
            color="primary"
            sx={{
              minWidth: 190,
              fontWeight: 600,
              borderRadius: "6px",
            }}
          >
            Continuar preenchendo
          </Button>

          <Button
            onClick={confirmarCancelamento}
            variant="contained"
            color="error"
            sx={{
              minWidth: 150,
              fontWeight: 600,
              borderRadius: "6px",
            }}
          >
            Descartar dados
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
